import React, { createContext, useContext, useState } from 'react';
import { IStoriesFolder } from '../../../../types';

export type IFolderNodeWithPath = IStoriesFolder & { path?: string };

type SidebarContextType = {
  data: IFolderNodeWithPath[];
  filterValue: string;
  setFilterValue: (value: string) => void;
};

const SidebarContext = createContext<SidebarContextType>({
  data: [],
  filterValue: '',
  setFilterValue: () => {},
});

export const useSidebarContext = () => useContext(SidebarContext);

type SidebarContextProviderProps = {
  data: IStoriesFolder[];
  children: React.ReactNode;
};

const hasActiveDescendent = (node: IStoriesFolder, id: string): boolean => {
  if (!node.children) return false;
  return node.children.some((child) => child.id === id || hasActiveDescendent(child, id));
};

const getDisplayNameForNodeId = (nodeId: string, nodes: IStoriesFolder[]): string => {
  for (const node of nodes) {
    if (node.id === nodeId) return node.displayName ?? '';
    if (node.children) {
      const found = getDisplayNameForNodeId(nodeId, node.children);
      if (found) return found;
    }
  }
  return '';
};

const getAbbreviatedBreadcrumbTrail = (nodeId: string, allNodes: IStoriesFolder[]): string =>
  allNodes.reduce((text, n) => {
    if (n.id === nodeId) {
      return text + (n.displayName ?? '');
    } else if (hasActiveDescendent(n, nodeId)) {
      const sep = n.children?.some((c) => c.id === nodeId) ? ' / ' : ' /... / ';
      return text + (n.displayName ?? '') + sep + getDisplayNameForNodeId(nodeId, allNodes);
    }
    return text;
  }, '');

const flattenMatchingNodes = (
  nodes: IStoriesFolder[],
  query: string,
  allNodes: IStoriesFolder[]
): IFolderNodeWithPath[] => {
  const lower = query.toLowerCase();
  return nodes.reduce<IFolderNodeWithPath[]>((acc, node) => {
    if (node.displayName?.toLowerCase().includes(lower)) {
      acc.push({
        ...node,
        children: [],
        displayName: getAbbreviatedBreadcrumbTrail(node.id, allNodes),
      });
    }
    if (node.children) {
      acc.push(...flattenMatchingNodes(node.children, query, allNodes));
    }
    return acc;
  }, []);
};

export const SidebarContextProvider = ({ data, children }: SidebarContextProviderProps) => {
  const [filterValue, setFilterValue] = useState('');
  const filteredData = filterValue
    ? flattenMatchingNodes(data, filterValue, data)
    : data;

  return (
    <SidebarContext.Provider value={{ data: filteredData, filterValue, setFilterValue }}>
      {children}
    </SidebarContext.Provider>
  );
};
